import axios from 'axios';
import { Task, User, Comment, ApiResponse, PaginatedResponse, AuthUser, Customer, ProjectRequestListItem, ProjectRequestDetail, ProjectRequestOptions, TaskGroup, ProjectDetail, ProjectListItem, ProjectCase, Role, Team, CustomerContact, CustomerContactListItem, TaskStatus, TaskPriority } from '../types';
import { FormSchema } from '../types/form';
import { showError } from '../utils/toast';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || 'https://srowb.mytiny.us/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Request interceptor for adding auth token
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('auth_token');
    if (token) {
      // Ensure headers object exists and set Authorization header safely
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('auth_token');
      showError('Session expired. Please login again.');
      window.location.href = '/login';
    } else if (error.response?.status === 403) {
      showError('You do not have permission to perform this action.');
    } else if (error.response?.status === 404) {
      showError('The requested resource was not found.');
    } else if (error.response?.status >= 500) {
      showError('Server error. Please try again later.');
    } else if (error.message === 'Network Error') {
      showError('Network error. Please check your connection.');
    }
    return Promise.reject(error);
  }
);

// User API
export const userApi = {
  search: async (query: string): Promise<User[]> => {
    const { data } = await api.get<User[]>(`/users/search?q=${encodeURIComponent(query)}`);
    return data;
  },
};

// Customers API
export const customersApi = {
  getAll: async (params?: { status?: string; page?: number; per_page?: number; search?: string }): Promise<PaginatedResponse<Customer>> => {
    const { data } = await api.get<PaginatedResponse<Customer>>('/customers', { params });
    return data;
  },
  getById: async (id: number): Promise<Customer> => {
    const { data } = await api.get<Customer>(`/customers/${id}`);
    return data;
  },
  create: async (payload: Partial<Customer>): Promise<Customer> => {
    const { data } = await api.post<Customer>('/customers', payload);
    return data;
  },
  update: async (id: number, payload: Partial<Customer>): Promise<Customer> => {
    const { data } = await api.put<Customer>(`/customers/${id}`, payload);
    return data;
  },
  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/customers/${id}`);
    return data;
  },
};

// Customer Contacts API
export const customerContactsApi = {
  getAll: async (params?: { customer_id?: number; page?: number }): Promise<PaginatedResponse<CustomerContactListItem>> => {
    const { data } = await api.get<PaginatedResponse<CustomerContactListItem>>('/customer-contacts', { params });
    return data;
  },
  getById: async (id: number): Promise<CustomerContact> => {
    const { data } = await api.get<CustomerContact>(`/customer-contacts/${id}`);
    return data;
  },
  create: async (payload: Partial<CustomerContact>): Promise<CustomerContact> => {
    const { data } = await api.post<CustomerContact>('/customer-contacts', payload);
    return data;
  },
  update: async (id: number, payload: Partial<CustomerContact>): Promise<CustomerContact> => {
    const { data } = await api.put<CustomerContact>(`/customer-contacts/${id}`, payload);
    return data;
  },
  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/customer-contacts/${id}`);
    return data;
  },
};

// Project Requests API
// Helper to convert payload to FormData
const createProjectRequestFormData = (payload: any): FormData => {
  const formData = new FormData();
  
  // Append all fields except files
  Object.keys(payload).forEach((key) => {
    if (key === 'request_email_file' || key === 'supporting_docs_file') {
      // Files are handled separately
      return;
    }
    
    const value = payload[key];
    if (value !== null && value !== undefined) {
      if (Array.isArray(value)) {
        // Handle arrays (like work_request) - Laravel expects work_request[] format
        value.forEach((item) => {
          formData.append(`${key}[]`, item);
        });
      } else if (typeof value === 'object') {
        // Handle objects (convert to JSON string)
        formData.append(key, JSON.stringify(value));
      } else if (typeof value === 'boolean') {
        // Convert boolean to string '1' or '0' for Laravel
        formData.append(key, value ? '1' : '0');
      } else {
        formData.append(key, value.toString());
      }
    }
  });
  
  // Append files if they exist
  if (payload.request_email_file instanceof File) {
    formData.append('request_email_file', payload.request_email_file);
  }
  if (payload.supporting_docs_file instanceof File) {
    formData.append('supporting_docs_file', payload.supporting_docs_file);
  }
  
  // Handle file removal flags
  if (payload.request_email_file_removed) {
    formData.append('request_email_file_removed', 'true');
  }
  if (payload.supporting_docs_file_removed) {
    formData.append('supporting_docs_file_removed', 'true');
  }
  
  return formData;
};

export const projectRequestsApi = {
  options: async (customerId?: number): Promise<ProjectRequestOptions> => {
    const { data } = await api.get<ProjectRequestOptions>('/project-requests/options', { params: { customer_id: customerId } });
    return data;
  },
  create: async (payload: Partial<ProjectRequestDetail> & { request_email_file?: File; supporting_docs_file?: File }): Promise<{ message: string; project_request: ProjectRequestDetail }> => {
    const formData = createProjectRequestFormData(payload);
    const { data } = await api.post<{ message: string; project_request: ProjectRequestDetail }>('/project-requests', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },
  list: async (params?: { status?: string; customer_id?: number; page?: number }): Promise<PaginatedResponse<ProjectRequestListItem>> => {
    const { data } = await api.get<PaginatedResponse<ProjectRequestListItem>>('/project-requests', { params });
    return data;
  },
  show: async (id: number): Promise<ProjectRequestDetail> => {
    const { data } = await api.get<ProjectRequestDetail>(`/project-requests/${id}`);
    return data;
  },
  update: async (id: number, payload: Partial<ProjectRequestDetail> & { request_email_file?: File; supporting_docs_file?: File; request_email_file_removed?: boolean; supporting_docs_file_removed?: boolean }): Promise<{ message: string; project_request: ProjectRequestDetail }> => {
    const formData = createProjectRequestFormData(payload);
    const { data } = await api.put<{ message: string; project_request: ProjectRequestDetail }>(`/project-requests/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },
};

// Deals API
export const dealsApi = {
  convertFromRequest: async (projectRequestId: number) => {
    const { data } = await api.post('/deals/convert-from-request/' + projectRequestId);
    return data;
  },
  getAll: async (params?: { stage?: string; customer_id?: number; page?: number; per_page?: number }) => {
    const { data } = await api.get('/deals', { params });
    return data;
  },
  getById: async (id: number) => {
    const { data } = await api.get(`/deals/${id}`);
    return data;
  },
  update: async (id: number, payload: any) => {
    const { data } = await api.put(`/deals/${id}`, payload);
    return data;
  },
};

// Task API
export const taskApi = {
  list: async (params?: {
    project_id?: number;
    status?: string;
    priority?: string;
    assignee_id?: number;
    assigned_to_team_id?: number;
    q?: string;
    page?: number;
  }): Promise<PaginatedResponse<Task>> => {
    const { data } = await api.get<PaginatedResponse<Task>>('/tasks', { params });
    return data;
  },

  getAll: async (params?: {
    status?: string;
    assignee_id?: number;
    page?: number;
  }): Promise<PaginatedResponse<Task>> => {
    const { data } = await api.get<PaginatedResponse<Task>>('/tasks', { params });
    return data;
  },

  getById: async (id: number): Promise<Task> => {
    const { data } = await api.get<Task>(`/tasks/${id}`);
    return data;
  },

  create: async (taskData: {
    project_id: number;
    task_name: string;
    description?: string;
    status?: TaskStatus;
    priority?: TaskPriority;
    due_date?: string;
    assignee_id?: number;
    assigned_to_team_id?: number;
  }): Promise<ApiResponse<Task>> => {
    const { data } = await api.post<ApiResponse<Task>>('/tasks', taskData);
    return data;
  },

  update: async (id: number, taskData: Partial<Task>): Promise<ApiResponse<Task>> => {
    const { data } = await api.put<ApiResponse<Task>>(`/tasks/${id}`, taskData);
    return data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/tasks/${id}`);
    return data;
  },
};

// Comment API
export const commentApi = {
  getAll: async (taskId?: number, caseId?: number, parentId?: number | null): Promise<PaginatedResponse<Comment>> => {
    const params: any = {};
    if (typeof taskId === 'number') params.task_id = taskId;
    if (typeof caseId === 'number') params.case_id = caseId;
    if (typeof parentId !== 'undefined') params.parent_id = parentId;
    const { data } = await api.get<PaginatedResponse<Comment>>('/comments', { params });
    return data;
  },
  getAllForCase: async (caseId: number, parentId?: number | null): Promise<PaginatedResponse<Comment>> => {
    return commentApi.getAll(undefined, caseId, parentId);
  },
  listReplies: async (parentId: number): Promise<PaginatedResponse<Comment>> => {
    const { data } = await api.get<PaginatedResponse<Comment>>('/comments', { params: { parent_id: parentId } });
    return data;
  },
  create: async (commentData: { task_id?: number; case_id?: number; raw_text: string; html: string; parent_id?: number | null; }): Promise<ApiResponse<Comment>> => {
    const { data } = await api.post<ApiResponse<Comment>>('/comments', commentData);
    return data;
  },
  createWithFiles: async (form: FormData): Promise<ApiResponse<Comment>> => {
    const { data } = await api.post<ApiResponse<Comment>>('/comments', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
  update: async (id: number, commentData: Partial<Comment>): Promise<ApiResponse<Comment>> => {
    const { data } = await api.put<ApiResponse<Comment>>(`/comments/${id}`, commentData);
    return data;
  },
  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/comments/${id}`);
    return data;
  },
  like: async (id: number): Promise<{ likes_count: number; is_liked: boolean }> => {
    const { data } = await api.post<{ likes_count: number; is_liked: boolean }>(`/comments/${id}/like`);
    return data;
  },
  unlike: async (id: number): Promise<{ likes_count: number; is_liked: boolean }> => {
    const { data } = await api.delete<{ likes_count: number; is_liked: boolean }>(`/comments/${id}/like`);
    return data;
  },
};

export const authApi = {
  login: async (email: string, password: string): Promise<{ token: string; user: AuthUser }> => {
    const { data } = await api.post<{ token: string; user: AuthUser }>('/login', { email, password });
    return data;
  },
  logout: async (): Promise<void> => {
    await api.post('/logout');
  },
  getUser: async (): Promise<AuthUser> => {
    const { data } = await api.get<AuthUser>('/user');
    return data;
  },
};

export const deviceTokenApi = {
  store: async (token: string, platform: string): Promise<ApiResponse<null>> => {
    const { data } = await api.post<ApiResponse<null>>('/device-tokens', { device_token: token, platform });
    return data;
  },
  destroy: async (token: string): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/device-tokens`, { params: { device_token: token } });
    return data;
  },
};

// Cases API
export const casesApi = {
  list: async (params?: {
    case_status?: string;
    project_id?: number;
    assigned_user_id?: number;
    case_priority?: string;
    page?: number;
  }): Promise<PaginatedResponse<ProjectCase>> => {
    const { data } = await api.get<PaginatedResponse<ProjectCase>>('/cases', { params });
    return data;
  },
  getById: async (id: number): Promise<ProjectCase> => {
    const { data } = await api.get<ProjectCase>(`/cases/${id}`);
    return data;
  },
  create: async (caseData: {
    case_subject: string;
    project_id?: number | null;
    case_priority?: 'low' | 'medium' | 'high';
    case_status?: string;
    assigned_user_id?: number | null;
    type?: string;
    due_date?: string | null;
    description?: string | null;
  }): Promise<ApiResponse<ProjectCase>> => {
    const { data } = await api.post<ApiResponse<ProjectCase>>('/cases', caseData);
    return data;
  },
  update: async (id: number, caseData: Partial<ProjectCase>): Promise<ApiResponse<ProjectCase>> => {
    const { data } = await api.put<ApiResponse<ProjectCase>>(`/cases/${id}`, caseData);
    return data;
  },
  updateStatus: async (id: number, case_status: string, notes?: string): Promise<ApiResponse<ProjectCase>> => {
    const { data } = await api.post<ApiResponse<ProjectCase>>(`/cases/${id}/update-status`, { case_status, notes });
    return data;
  },
  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/cases/${id}`);
    return data;
  },
};

// Projects/Board
export const projectsApi = {
  list: async (params?: { customer_id?: number; page?: number }): Promise<PaginatedResponse<ProjectListItem>> => {
    const { data } = await api.get<PaginatedResponse<ProjectListItem>>('/projects', { params });
    return data;
  },
  getById: async (id: number): Promise<ProjectDetail & { task_groups?: TaskGroup[] }> => {
    const { data } = await api.get<ProjectDetail & { task_groups?: TaskGroup[] }>(`/projects/${id}`);
    return data;
  },
  listGroups: async (projectId: number): Promise<TaskGroup[]> => {
    const { data } = await api.get<TaskGroup[]>(`/projects/${projectId}/task-groups`);
    return data;
  },
  createGroup: async (projectId: number, name: string): Promise<TaskGroup> => {
    const { data } = await api.post<TaskGroup>(`/projects/${projectId}/task-groups`, { name });
    return data;
  },
  updateGroup: async (projectId: number, groupId: number, payload: Partial<Pick<TaskGroup, 'name' | 'sort_order'>>): Promise<TaskGroup> => {
    const { data } = await api.put<TaskGroup>(`/projects/${projectId}/task-groups/${groupId}`, payload);
    return data;
  },
  deleteGroup: async (projectId: number, groupId: number): Promise<void> => {
    await api.delete(`/projects/${projectId}/task-groups/${groupId}`);
  },
  update: async (id: number, payload: Partial<ProjectDetail>): Promise<ProjectDetail> => {
    const { data } = await api.put<ProjectDetail>(`/projects/${id}`, payload);
    return data;
  },
};

// User Management API
export const userManagementApi = {
  list: async (params?: {
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    search?: string;
    status?: 'active' | 'inactive';
    role_id?: number;
  }): Promise<PaginatedResponse<User>> => {
    const { data } = await api.get<PaginatedResponse<User>>('/users', { params });
    return data;
  },
  getById: async (id: number): Promise<User> => {
    const { data } = await api.get<User>(`/users/${id}`);
    return data;
  },
  create: async (userData: {
    name: string;
    email: string;
    password: string;
    status?: 'active' | 'inactive';
    roles?: number[];
  }): Promise<ApiResponse<User>> => {
    const { data } = await api.post<ApiResponse<User>>('/users', userData);
    return data;
  },
  update: async (id: number, userData: Partial<{
    name: string;
    email: string;
    password: string;
    status: 'active' | 'inactive';
    roles: number[];
  }>): Promise<ApiResponse<User>> => {
    const { data } = await api.put<ApiResponse<User>>(`/users/${id}`, userData);
    return data;
  },
  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/users/${id}`);
    return data;
  },
  updateStatus: async (id: number, status: 'active' | 'inactive'): Promise<ApiResponse<User>> => {
    const { data } = await api.post<ApiResponse<User>>(`/users/${id}/status`, { status });
    return data;
  },
  bulkUpdateStatus: async (userIds: number[], status: 'active' | 'inactive'): Promise<ApiResponse<null>> => {
    const { data } = await api.post<ApiResponse<null>>('/users/bulk-update-status', { user_ids: userIds, status });
    return data;
  },
  assignRoles: async (id: number, roleIds: number[]): Promise<ApiResponse<User>> => {
    const { data } = await api.post<ApiResponse<User>>(`/users/${id}/assign-roles`, { roles: roleIds });
    return data;
  },
  bulkAssignRoles: async (userIds: number[], roleIds: number[]): Promise<ApiResponse<null>> => {
    const { data } = await api.post<ApiResponse<null>>('/users/bulk-assign-roles', { user_ids: userIds, roles: roleIds });
    return data;
  },
  resetPassword: async (id: number): Promise<ApiResponse<{ new_password?: string }>> => {
    const { data } = await api.post<ApiResponse<{ new_password?: string }>>(`/users/${id}/reset-password`);
    return data;
  },
};

// Role API
export const roleApi = {
  list: async (params?: {
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
    search?: string;
  }): Promise<PaginatedResponse<Role>> => {
    const { data } = await api.get<PaginatedResponse<Role>>('/roles', { params });
    return data;
  },
  getById: async (id: number): Promise<Role> => {
    const { data } = await api.get<Role>(`/roles/${id}`);
    return data;
  },
  create: async (roleData: {
    name: string;
    permissions?: number[];
  }): Promise<ApiResponse<Role>> => {
    const { data } = await api.post<ApiResponse<Role>>('/roles', roleData);
    return data;
  },
  update: async (id: number, roleData: Partial<{
    name: string;
    permissions: number[];
  }>): Promise<ApiResponse<Role>> => {
    const { data } = await api.put<ApiResponse<Role>>(`/roles/${id}`, roleData);
    return data;
  },
  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/roles/${id}`);
    return data;
  },
  syncPermissions: async (id: number, permissionIds: number[]): Promise<ApiResponse<Role>> => {
    const { data } = await api.post<ApiResponse<Role>>(`/roles/${id}/sync-permissions`, { permissions: permissionIds });
    return data;
  },
};

// Permission API
export const permissionApi = {
  list: async (): Promise<{ permissions: Array<{ module: string; permissions: Array<{ id: number; name: string; action: string }> }> }> => {
    const { data } = await api.get<{ permissions: Array<{ module: string; permissions: Array<{ id: number; name: string; action: string }> }> }>('/permissions');
    return data;
  },
  getModules: async (): Promise<{ modules: string[] }> => {
    const { data } = await api.get<{ modules: string[] }>('/permissions/modules');
    return data;
  },
  getByModule: async (module: string): Promise<{ module: string; permissions: Array<{ id: number; name: string; action: string }> }> => {
    const { data } = await api.get<{ module: string; permissions: Array<{ id: number; name: string; action: string }> }>(`/permissions/module/${module}`);
    return data;
  },
};

// Team API
export const teamApi = {
  list: async (params?: {
    page?: number;
    per_page?: number;
    search?: string;
  }): Promise<PaginatedResponse<Team>> => {
    const { data } = await api.get<PaginatedResponse<Team>>('/teams', { params });
    return data;
  },
  getById: async (id: number): Promise<Team> => {
    const { data } = await api.get<Team>(`/teams/${id}`);
    return data;
  },
  create: async (teamData: {
    name: string;
    description?: string;
    member_ids?: number[];
  }): Promise<ApiResponse<Team>> => {
    const { data } = await api.post<ApiResponse<Team>>('/teams', teamData);
    return data;
  },
  update: async (id: number, teamData: Partial<{
    name: string;
    description?: string;
  }>): Promise<ApiResponse<Team>> => {
    const { data } = await api.put<ApiResponse<Team>>(`/teams/${id}`, teamData);
    return data;
  },
  delete: async (id: number): Promise<ApiResponse<null>> => {
    const { data } = await api.delete<ApiResponse<null>>(`/teams/${id}`);
    return data;
  },
  addMembers: async (id: number, userIds: number[]): Promise<ApiResponse<Team>> => {
    const { data } = await api.post<ApiResponse<Team>>(`/teams/${id}/add-members`, { user_ids: userIds });
    return data;
  },
  removeMembers: async (id: number, userIds: number[]): Promise<ApiResponse<Team>> => {
    const { data } = await api.post<ApiResponse<Team>>(`/teams/${id}/remove-members`, { user_ids: userIds });
    return data;
  },
};

// Forms API
export const formsApi = {
  list: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
    isTemplate?: boolean;
    isActive?: boolean;
  }): Promise<PaginatedResponse<FormSchema>> => {
    const { data } = await api.get<PaginatedResponse<FormSchema>>('/forms', { params });
    return data;
  },
  getById: async (id: number): Promise<FormSchema> => {
    const { data } = await api.get<FormSchema>(`/forms/${id}`);
    return data;
  },
  create: async (formData: {
    title: string;
    description?: string;
    handler: string;
    fields: Array<{
      id: string;
      type: string;
      label: string;
      name: string;
      placeholder?: string;
      defaultValue?: string;
      required?: boolean;
      order?: number;
      options?: Array<{ label: string; value: string }>;
      validation?: {
        minLength?: number;
        maxLength?: number;
        min?: number;
        max?: number;
        pattern?: string;
        patternMessage?: string;
        fileMaxSize?: number;
        fileTypes?: string[];
        customMessage?: string;
      };
      conditionalLogic?: Array<{
        field: string;
        operator: string;
        value: string | number;
        action: 'show' | 'hide';
      }>;
      calculation?: {
        formula: string;
        fields: string[];
        decimals?: number;
      };
      helpText?: string;
      width?: 'full' | 'half' | 'third';
    }>;
    is_template?: boolean;
    is_active?: boolean;
    medical_category?: string;
  }): Promise<FormSchema> => {
    const { data } = await api.post<FormSchema>('/forms', formData);
    return data;
  },
  update: async (id: number, formData: Partial<{
    title: string;
    description?: string;
    handler?: string;
    fields?: Array<{
      id: string;
      type: string;
      label: string;
      name: string;
      placeholder?: string;
      defaultValue?: string;
      required?: boolean;
      order?: number;
      options?: Array<{ label: string; value: string }>;
      validation?: any;
      conditionalLogic?: any[];
      calculation?: any;
      helpText?: string;
      width?: string;
    }>;
    medical_category?: string;
  }>): Promise<FormSchema> => {
    const { data } = await api.put<FormSchema>(`/forms/${id}`, formData);
    return data;
  },
  getByHandler: async (handler: string): Promise<FormSchema> => {
    const { data } = await api.get<FormSchema>(`/forms/handler/${handler}`);
    return data;
  },
  delete: async (id: number): Promise<void> => {
    await api.delete(`/forms/${id}`);
  },
  submit: async (formId: number, payload: {
    data: Record<string, any>;
    metadata?: Record<string, any>;
  }): Promise<{ id: number; message: string }> => {
    const { data } = await api.post<{ id: number; message: string }>(`/forms/${formId}/submit`, payload);
    return data;
  },
  getSubmissions: async (formId: number, params?: {
    page?: number;
    limit?: number;
    status?: string;
    startDate?: string;
    endDate?: string;
    [key: string]: any;
  }): Promise<PaginatedResponse<any>> => {
    const { data } = await api.get<PaginatedResponse<any>>(`/forms/${formId}/submissions`, { params });
    return data;
  },
  getSubmissionById: async (formId: number, submissionId: number): Promise<any> => {
    const { data } = await api.get(`/forms/${formId}/submissions/${submissionId}`);
    return data;
  },
  generateShareKey: async (formId: number): Promise<{ shareable_link: string; key: string; form_id: number; handler: string; title: string }> => {
    const { data } = await api.post<{ shareable_link: string; key: string; form_id: number; handler: string; title: string }>(`/forms/${formId}/generate-share-key`);
    return data;
  },
  verifyShareKey: async (key: string): Promise<{ success: boolean; message: string; form_id: number; form: FormSchema }> => {
    const { data } = await api.get<{ success: boolean; message: string; form_id: number; form: FormSchema }>(`/forms/verify-share-key?key=${key}`);
    return data;
  },
};

export default api;
